import ChevronLeft from '@assets/icons/chevron-left.svg';
import ChevronRight from '@assets/icons/chevron-right.svg';
import i18n from '@/utils/i18n';
import PencilIcon from '@assets/icons/pencil.svg';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Action,
  SmartOrderAction,
  SmartOrderState,
} from './hooks/use-smart-order-reducer';
import { ApolloQueryResult } from '@apollo/client';
import { Button, Props as ButtonProps } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { FlatList, Platform, Text, View } from 'react-native';
import { ItemListOption } from '@/contexts/item-context-types';
import { LayoutSize, useLayoutSizeValue } from '@/contexts/layout-context';
import { ListOption } from '@/components/list-item';
import { Order_SmartOrderQuery } from '@/types';
import { SCREEN_PADDING, shadow, TYPOGRAPHY } from '@/styles';
import { SmartOrderIndividualInput } from './smart-order-individual-input';
import { SmartOrderListItem } from './smart-order-list-item';
import { SmartOrderListItemLoading } from './smart-order-list-item-loading';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useToasts } from '@/contexts/toast-context';

export const SMART_ORDER_TRACKING_KEY = 'smart_order_results';
const LAYOUT_SIZE_TO_COLUMNS_MAP = {
  [LayoutSize.SMALL]: 1,
  [LayoutSize.SMALL_TABLET]: 1,
  [LayoutSize.MEDIUM]: 1,
  [LayoutSize.LARGE]: 2,
};

export type SmartOrderListOption = ListOption<{
  index: number;
  quantity: number;
  results: ItemListOption[];
}>;

interface Props {
  dispatch: React.Dispatch<Action>;
  getSmartOrderResults: (
    items: string,
    isRestrictedSearch: boolean,
  ) => Promise<ApolloQueryResult<Order_SmartOrderQuery>>;
  option: SmartOrderListOption;
  state: SmartOrderState;
}

export const SmartOrderResult: React.FC<Props> = ({
  dispatch,
  getSmartOrderResults,
  option,
  state,
}) => {
  const { selectItemListOption } = useItemDetailContext();
  const { showErrorToast } = useToasts();
  const [styles] = useStyles();

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listWidth, setListWidth] = useState(0);

  const horizontalListRef = useRef<FlatList>(null);
  const listContentWidthRef = useRef(0);
  const scrollOffsetRef = useRef(0);

  const visibleItemCount = useLayoutSizeValue(LAYOUT_SIZE_TO_COLUMNS_MAP);
  const itemCount = option.metadata!.results.length;
  const renderArrows = Platform.OS === 'web' && itemCount > visibleItemCount;
  const itemWidth = useMemo(() => {
    return Math.round(listWidth / visibleItemCount);
  }, [listWidth, visibleItemCount]);
  const hasNoItemResults = itemCount === 0;

  useEffect(() => {
    if (loading && horizontalListRef.current) {
      horizontalListRef.current.scrollToOffset({
        animated: false,
        offset: 0,
      });
    }
  });

  const onLeftArrowPress = () => {
    horizontalListRef.current?.scrollToOffset({
      animated: true,
      offset: Math.max(0, scrollOffsetRef.current - itemWidth),
    });
  };

  const onRightArrowPress = () => {
    horizontalListRef.current?.scrollToOffset({
      animated: true,
      offset: Math.min(
        listContentWidthRef.current,
        scrollOffsetRef.current + itemWidth,
      ),
    });
  };

  const arrowButtonProps: Partial<ButtonProps> = {
    iconProps: {
      height: 16,
      width: 7,
    },
    rounded: true,
    size: 'small',
    style: styles.arrowButton,
    type: 'secondary',
  };

  return (
    <View style={styles.base}>
      <View style={styles.header}>
        <View style={styles.inputWrapper}>
          {isEditing ? (
            <SmartOrderIndividualInput
              initialValue={option.value as string}
              loading={loading}
              onBlur={() => setIsEditing(false)}
              onSubmit={async (item) => {
                const metadata = option.metadata!;
                const requestedItem = `${metadata.quantity} ${item}`;
                setLoading(true);

                try {
                  const { data } = await getSmartOrderResults(
                    requestedItem,
                    false,
                  );
                  dispatch({
                    payload: {
                      groupIndex: metadata.index,
                      results:
                        data.getSmartOrderResults.length > 0
                          ? data.getSmartOrderResults
                          : [
                              {
                                quantity: 0,
                                search_term: item,
                                variant_pack: null,
                              },
                            ],
                    },
                    type: SmartOrderAction.EDIT_ITEM,
                  });
                } catch {
                  showErrorToast();
                }

                setLoading(false);
              }}
            />
          ) : (
            <>
              <Text style={TYPOGRAPHY.h3}>{option.title}</Text>
              <Button
                Icon={PencilIcon}
                onPress={() => setIsEditing(true)}
                size="small"
                style={styles.editButton}
                testID="smart-order-edit-button"
                trackingData={null}
                type="tertiary"
              />
            </>
          )}
        </View>
        {renderArrows ? (
          <View style={styles.arrowWrapper}>
            <Button
              Icon={ChevronLeft}
              onPress={onLeftArrowPress}
              testID="horizontal-header-left-arrow"
              trackingData={null}
              {...arrowButtonProps}
            />
            <Button
              Icon={ChevronRight}
              onPress={onRightArrowPress}
              testID="horizontal-header-right-arrow"
              trackingData={null}
              {...arrowButtonProps}
            />
          </View>
        ) : null}
      </View>
      {hasNoItemResults ? (
        <View style={styles.noItemResults}>
          {loading ? (
            <SmartOrderListItemLoading />
          ) : (
            <Text style={TYPOGRAPHY.p}>
              {i18n.t('smart_order.no_item_results')}
            </Text>
          )}
        </View>
      ) : (
        <FlatList<ItemListOption>
          contentContainerStyle={styles.horizontalList}
          data={option.metadata!.results}
          decelerationRate="fast"
          horizontal
          onContentSizeChange={(width) => {
            listContentWidthRef.current = width;
          }}
          onLayout={(e) => {
            setListWidth(e.nativeEvent.layout.width);
          }}
          onScroll={(e) => {
            const offsetX = e.nativeEvent.contentOffset.x;
            scrollOffsetRef.current = offsetX;
          }}
          ref={horizontalListRef}
          renderItem={({ item }) => {
            if (loading) {
              return <SmartOrderListItemLoading />;
            }

            const variantPackUUID = item.metadata!.variantPackUUID;
            return (
              <SmartOrderListItem
                item={item}
                onSelect={() => {
                  selectItemListOption(
                    item,
                    {
                      source: SMART_ORDER_TRACKING_KEY,
                    },
                    {
                      // Hide quantity in item details
                      renderActions: () => null,
                    },
                  );
                }}
                quantity={state.quantityMap[variantPackUUID] ?? 0}
                updateQuantity={(quantity) =>
                  dispatch({
                    payload: {
                      quantity,
                      variantPackUUID,
                    },
                    type: SmartOrderAction.UPDATE_QUANTITY,
                  })
                }
              />
            );
          }}
          scrollEventThrottle={100}
          showsHorizontalScrollIndicator={false}
          testID="smart-order-result-item-list"
        />
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  arrowButton: {
    backgroundColor: theme.colors.white,
    height: 40,
    width: 40,
    ...shadow({
      color: theme.colors.black,
      elevation: 5,
      opacity: 0.1,
      radius: 4,
      x: 0,
      y: 0,
    }),
  },
  arrowWrapper: {
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 8,
  },
  base: {
    flex: 1,
    gap: 8,
  },
  editButton: {
    paddingLeft: 0,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
    paddingHorizontal: SCREEN_PADDING,
  },
  horizontalList: {
    gap: 8,
    paddingHorizontal: SCREEN_PADDING,
    paddingVertical: SCREEN_PADDING / 4,
  },
  inputWrapper: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
  },
  noItemResults: {
    marginBottom: 8,
    paddingHorizontal: SCREEN_PADDING,
  },
}));
